import type { AppVueRouter } from '@modules/core/services/router/types';
import type { TranslationKeysMapper } from '@modules/i18n/types';
import type { AppMenu } from '@modules/sidebar/utils';
import { AppModule } from '@modules/core/plugins/AppModule';
import { useI18n } from '@modules/i18n/composables';
import { PLAYERS_PERMISSIONS } from '@modules/players/constants';
import { ADMIN_PARTNERS_MENU_ITEM, AFFILIATE_REPORTS_MENU_ITEM, TRANSLATION_GROUP_SIDEBAR } from '@modules/sidebar/constants';

import {
  ADMIN_CREATIVE_REPORTS_ROUTE_NAME,
  ADMIN_REPORTS_PLAYER_PROFILE,
  ADMIN_REPORTS_PLAYER_PROFILE_ACTIVITY_TAB,
  ADMIN_REPORTS_PLAYER_PROFILE_MAIN_DATA_TAB,
  ADMIN_REPORTS_PLAYER_PROFILE_TRACKER_TAB,
  ADMIN_REPORTS_PLAYER_PROFILE_TRACKING_HISTORY_EDIT_ROUTE_NAME,
  ADMIN_REPORTS_ROUTE_NAME,
  ADMIN_SUBAFFILIATE_REPORTS_ROUTE_NAME,
  AFFILIATE_CREATIVE_REPORTS_ROUTE_NAME,
  AFFILIATE_REPORTS_ROUTE_NAME,
  AFFILIATE_SUBAFFILIATE_REPORTS_ROUTE_NAME,
  REPORTS_PERMISSIONS,
  TRANSLATION_GROUP_CREATIVE_REPORTS,
  TRANSLATION_GROUP_REPORTS,
  TRANSLATION_GROUP_SUBAFFILIATE_REPORTS,
} from './constants';
import { ReportType } from './enums';
import { creativeReportsTranslations, reportsTranslations, subaffiliateReportsTranslations } from './translations';

export class ReportsModule extends AppModule {
  addRoutes(router: AppVueRouter): void {
    router.addAdminRoute({
      name: ADMIN_REPORTS_ROUTE_NAME,
      path: 'reports',
      permission: REPORTS_PERMISSIONS.REPORTS_GENERATE_REPORT,
      props: {
        reportType: ReportType.REPORTS,
        translationKey: TRANSLATION_GROUP_REPORTS,
        isStructureAllowed: true,
      },
      component: () => import('./pages/ReportsPage/ReportsPage.vue'),
    });

    router.addChildRoute(ADMIN_REPORTS_ROUTE_NAME, {
      name: ADMIN_REPORTS_PLAYER_PROFILE,
      path: 'player-profile/:id/:merchant',
      component: () => import('@modules/players/submodules/PlayerProfile/pages/PlayerProfileDialogPage/PlayerProfileDialogPage.vue'),
      permission: PLAYERS_PERMISSIONS.PLAYERS_SHOW,
      redirect: { name: ADMIN_REPORTS_PLAYER_PROFILE_TRACKER_TAB },
      props: {
        backTo: { name: ADMIN_REPORTS_ROUTE_NAME },
        mainDataRoute: { name: ADMIN_REPORTS_PLAYER_PROFILE_MAIN_DATA_TAB },
        activityRoute: { name: ADMIN_REPORTS_PLAYER_PROFILE_ACTIVITY_TAB },
        trackerRoute: { name: ADMIN_REPORTS_PLAYER_PROFILE_TRACKER_TAB },
        editHistoryRoute: { name: ADMIN_REPORTS_PLAYER_PROFILE_TRACKING_HISTORY_EDIT_ROUTE_NAME },
      },
    });

    router.addChildRoute(ADMIN_REPORTS_PLAYER_PROFILE, {
      name: ADMIN_REPORTS_PLAYER_PROFILE_MAIN_DATA_TAB,
      path: 'main_data',
      component: () => import('@modules/players/submodules/PlayerProfile/submodules/PlayerMainData/pages/PlayerMainDataPage/PlayerMainDataPage.vue'),
    });

    router.addChildRoute(ADMIN_REPORTS_PLAYER_PROFILE, {
      name: ADMIN_REPORTS_PLAYER_PROFILE_ACTIVITY_TAB,
      path: 'player_activity',
      component: () => import('@modules/players/submodules/PlayerProfile/submodules/PlayerActivity/pages/PlayerActivityPage/PlayerActivityPage.vue'),
    });

    router.addChildRoute(ADMIN_REPORTS_PLAYER_PROFILE, {
      name: ADMIN_REPORTS_PLAYER_PROFILE_TRACKER_TAB,
      path: 'player_tracker',
      component: () => import('@modules/players/submodules/PlayerProfile/submodules/PlayerTracker/pages/PlayerTrackerPage/PlayerTrackerPage.vue'),
    });

    router.addChildRoute(ADMIN_REPORTS_PLAYER_PROFILE_TRACKER_TAB, {
      name: ADMIN_REPORTS_PLAYER_PROFILE_TRACKING_HISTORY_EDIT_ROUTE_NAME,
      path: 'change_history/:historyId',
      component: () => import('@modules/players/submodules/PlayerProfile/pages/PlayerTrackingHistoryEditPage/PlayerTrackingHistoryEditPage.vue'),
    });

    router.addAdminRoute({
      name: ADMIN_SUBAFFILIATE_REPORTS_ROUTE_NAME,
      path: 'subaffiliate-reports',
      permission: REPORTS_PERMISSIONS.SUBAFFILIATE_REPORTS_GENERATE_REPORT,
      props: {
        reportType: ReportType.SUBAFFILIATE_REPORTS,
        translationKey: TRANSLATION_GROUP_SUBAFFILIATE_REPORTS,
        isStructureAllowed: false,
      },
      component: () => import('./pages/ReportsPage/ReportsPage.vue'),
    });

    router.addAdminRoute({
      name: ADMIN_CREATIVE_REPORTS_ROUTE_NAME,
      path: 'creative-reports',
      permission: REPORTS_PERMISSIONS.CREATIVE_REPORTS_GENERATE_REPORT,
      props: {
        reportType: ReportType.CREATIVE_REPORTS,
        translationKey: TRANSLATION_GROUP_CREATIVE_REPORTS,
        isStructureAllowed: false,
      },
      component: () => import('./pages/ReportsPage/ReportsPage.vue'),
    });

    router.addAffiliateRoute({
      name: AFFILIATE_REPORTS_ROUTE_NAME,
      path: 'reports',
      permission: REPORTS_PERMISSIONS.REPORTS_GENERATE_REPORT,
      props: {
        reportType: ReportType.REPORTS,
        translationKey: TRANSLATION_GROUP_REPORTS,
        isStructureAllowed: true,
      },
      component: () => import('./pages/ReportsPage/ReportsPage.vue'),
    });

    router.addAffiliateRoute({
      name: AFFILIATE_SUBAFFILIATE_REPORTS_ROUTE_NAME,
      path: 'subaffiliate-reports',
      permission: REPORTS_PERMISSIONS.SUBAFFILIATE_REPORTS_GENERATE_REPORT,
      props: {
        reportType: ReportType.SUBAFFILIATE_REPORTS,
        translationKey: TRANSLATION_GROUP_SUBAFFILIATE_REPORTS,
        isStructureAllowed: false,
      },
      component: () => import('./pages/ReportsPage/ReportsPage.vue'),
    });

    router.addAffiliateRoute({
      name: AFFILIATE_CREATIVE_REPORTS_ROUTE_NAME,
      path: 'creative-reports',
      permission: REPORTS_PERMISSIONS.CREATIVE_REPORTS_GENERATE_REPORT,
      props: {
        reportType: ReportType.CREATIVE_REPORTS,
        translationKey: TRANSLATION_GROUP_CREATIVE_REPORTS,
        isStructureAllowed: false,
      },
      component: () => import('./pages/ReportsPage/ReportsPage.vue'),
    });
  }

  addMenu(menu: AppMenu): void {
    const { getTPath } = useI18n();

    menu.addChildAdminMenuItem(ADMIN_PARTNERS_MENU_ITEM, {
      caption: getTPath(TRANSLATION_GROUP_SIDEBAR, 'PARTNERS_REPORTS'),
      to: {
        name: ADMIN_REPORTS_ROUTE_NAME,
      },
      permission: REPORTS_PERMISSIONS.REPORTS_GENERATE_REPORT,
      id: ADMIN_REPORTS_ROUTE_NAME,
      order: 70,
    });

    menu.addChildAdminMenuItem(ADMIN_PARTNERS_MENU_ITEM, {
      caption: getTPath(TRANSLATION_GROUP_SIDEBAR, 'PARTNERS_SUBAFFILIATE_REPORTS'),
      to: {
        name: ADMIN_SUBAFFILIATE_REPORTS_ROUTE_NAME,
      },
      permission: REPORTS_PERMISSIONS.SUBAFFILIATE_REPORTS_GENERATE_REPORT,
      id: ADMIN_SUBAFFILIATE_REPORTS_ROUTE_NAME,
      order: 80,
    });

    menu.addChildAdminMenuItem(ADMIN_PARTNERS_MENU_ITEM, {
      caption: getTPath(TRANSLATION_GROUP_SIDEBAR, 'PARTNERS_CREATIVE_REPORTS'),
      to: {
        name: ADMIN_CREATIVE_REPORTS_ROUTE_NAME,
      },
      permission: REPORTS_PERMISSIONS.CREATIVE_REPORTS_GENERATE_REPORT,
      id: ADMIN_CREATIVE_REPORTS_ROUTE_NAME,
      order: 90,
    });

    menu.addChildAffiliateMenuItem(AFFILIATE_REPORTS_MENU_ITEM, {
      caption: getTPath(TRANSLATION_GROUP_SIDEBAR, 'REPORTS_REPORTS'),
      to: {
        name: AFFILIATE_REPORTS_ROUTE_NAME,
      },
      permission: REPORTS_PERMISSIONS.REPORTS_GENERATE_REPORT,
      id: AFFILIATE_REPORTS_ROUTE_NAME,
      order: 10,
    });

    menu.addChildAffiliateMenuItem(AFFILIATE_REPORTS_MENU_ITEM, {
      caption: getTPath(TRANSLATION_GROUP_SIDEBAR, 'REPORTS_SUBAFFILIATE_REPORTS'),
      to: {
        name: AFFILIATE_SUBAFFILIATE_REPORTS_ROUTE_NAME,
      },
      permission: REPORTS_PERMISSIONS.SUBAFFILIATE_REPORTS_GENERATE_REPORT,
      id: AFFILIATE_SUBAFFILIATE_REPORTS_ROUTE_NAME,
      order: 20,
    });

    menu.addChildAffiliateMenuItem(AFFILIATE_REPORTS_MENU_ITEM, {
      caption: getTPath(TRANSLATION_GROUP_SIDEBAR, 'REPORTS_CREATIVE_REPORTS'),
      to: {
        name: AFFILIATE_CREATIVE_REPORTS_ROUTE_NAME,
      },
      permission: REPORTS_PERMISSIONS.CREATIVE_REPORTS_GENERATE_REPORT,
      id: AFFILIATE_CREATIVE_REPORTS_ROUTE_NAME,
      order: 30,
    });
  }

  addTranslationKeys(mapper: TranslationKeysMapper): void {
    mapper.addTranslationsMap(TRANSLATION_GROUP_REPORTS, reportsTranslations);
    mapper.addTranslationsMap(TRANSLATION_GROUP_SUBAFFILIATE_REPORTS, subaffiliateReportsTranslations);
    mapper.addTranslationsMap(TRANSLATION_GROUP_CREATIVE_REPORTS, creativeReportsTranslations);
  }
}
